import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import { Pause, Play, X } from 'lucide-react';
import { Spinner } from 'react-bootstrap';
import tripsService from '../../services/trips/trips.service';
import { ITripActivity } from '../../Models/trips/trip-activity.model';
import { ITripCardSlide } from '../../Models/trips/trip-summary.model';
import { AuthenticatedImage } from '../UI/authenticated-image';
import { useTripsOptional } from '../contexts/trips-context';
import config from '../../config';
import styles from './trip-reel-view.module.scss';

const DEFAULT_SLIDE_DURATION_MS = 5000;

/** Resolve proxy paths to fully-qualified API URLs (auth required). */
function resolveUrl(url: string | undefined): string | undefined {
  if (!url) return undefined;
  if (url.startsWith('/api/')) {
    const apiBase = (
      config.TRAVELHEARTS_API_URL ?? 'http://localhost:8080/api/v1'
    ).replace(/\/api\/v\d.*$/, '');
    const normalised = url.replace(/^\/api\/(?!v\d)/, '/api/v1/');
    return apiBase + normalised;
  }
  return url;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function getSlideImageUrl(
  activity: ITripActivity,
  cardSlideMap: Map<string, string>
): string | undefined {
  const id = activity.id ?? '';
  if (id && cardSlideMap.has(id)) {
    const url = resolveUrl(cardSlideMap.get(id));
    console.debug(
      `[ReelSlide] ${activity.title} — using cardSlideMap url:`,
      url
    );
    return url;
  }
  const displayImages = activity.displayState?.displayImages;
  if (activity.displayState?.imagesResolved && displayImages?.length) {
    const url = resolveUrl(displayImages[0]);
    console.debug(
      `[ReelSlide] ${activity.title} — using displayImages url:`,
      url
    );
    return url;
  }
  const url = resolveUrl(activity.imageUrl ?? undefined);
  console.debug(
    `[ReelSlide] ${activity.title} — fallback imageUrl:`,
    url,
    '(no cardSlideMap match, imagesResolved:',
    activity.displayState?.imagesResolved,
    ')'
  );
  return url;
}

function getSlideDuration(activity: ITripActivity): number {
  if (activity.displayState?.displayMode === 'video_clip') {
    return activity.displayState.clipMaxDurationMs ?? DEFAULT_SLIDE_DURATION_MS;
  }
  return DEFAULT_SLIDE_DURATION_MS;
}

// ── Component ─────────────────────────────────────────────────────────────────

export interface TripReelViewProps {
  /** The trip ID to load activities from. */
  tripId: string;
  /**
   * Pre-loaded card slides (activityId → imageUrl) from the caller.
   * When provided the reel can show images immediately without waiting
   * for the trip fetch to complete.
   */
  cardSlides?: ITripCardSlide[];
  /** Called when the user dismisses the reel (X button, Escape key). */
  onClose: () => void;
  /**
   * Optional activity ID to start on (honours ?activity= query param when
   * used as a route page too).
   */
  startActivityId?: string;
}

export function TripReelView({
  tripId,
  cardSlides: cardSlidesProp,
  onClose,
  startActivityId
}: TripReelViewProps) {
  const [searchParams] = useSearchParams();
  const { trips } = useTripsOptional() ?? { trips: [] };

  // Build activityId → imageUrl map.
  // Priority 1: cardSlides passed directly as a prop (from TripCard click)
  // Priority 2: trips context (if TripsProvider is in scope)
  const cardSlideMap = React.useMemo(() => {
    const contextSlides = trips.find((t) => t.id === tripId)?.cardSlides;
    const source = cardSlidesProp ?? contextSlides ?? [];
    const map = new Map<string, string>();
    for (const slide of source) {
      if (slide.activityId && slide.imageUrl) {
        map.set(slide.activityId, slide.imageUrl);
      }
    }
    console.debug('[TripReelView] cardSlideMap built:', {
      tripId,
      sourceLength: source.length,
      fromProp: !!cardSlidesProp,
      fromContext: !!contextSlides,
      mapEntries: [...map.entries()].map(([id, url]) => ({ id, url }))
    });
    return map;
  }, [cardSlidesProp, trips, tripId]);

  const [activities, setActivities] = useState<ITripActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [showPauseIcon, setShowPauseIcon] = useState(false);
  const [progressKey, setProgressKey] = useState(0); // bump to restart animation

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const touchStartXRef = useRef<number | null>(null);
  const pauseIconTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Load trip ───────────────────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        console.debug('[TripReelView] loading trip', tripId);
        const trip = await tripsService.getTripById(tripId);
        const visible = (trip.activities ?? []).filter((a) => !a.deleted);
        console.debug('[TripReelView] trip loaded:', {
          activityCount: visible.length,
          activities: visible.map((a) => ({
            id: a.id,
            title: a.title,
            imageUrl: a.imageUrl,
            displayImages: a.displayState?.displayImages,
            imagesResolved: a.displayState?.imagesResolved
          }))
        });
        setActivities(visible);

        // Honour startActivityId prop first, then ?activity= query param
        const startId = startActivityId ?? searchParams.get('activity');
        if (startId) {
          const idx = visible.findIndex((a) => a.id === startId);
          if (idx > 0) setCurrentIndex(idx);
        }
      } catch (err) {
        console.error('[TripReelView] failed to load trip:', err);
        setError('Could not load trip.');
      } finally {
        setLoading(false);
      }
    })();
  }, [tripId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Auto-advance ────────────────────────────────────────────────────────────
  // Close on Escape — stable reference via useCallback
  const handleClose = useCallback(() => onClose(), [onClose]);

  const goNext = useCallback(() => {
    setCurrentIndex((prev) => {
      if (prev >= activities.length - 1) return prev; // stay on last
      return prev + 1;
    });
    setProgressKey((k) => k + 1);
  }, [activities.length]);

  const goPrev = useCallback(() => {
    setCurrentIndex((prev) => Math.max(0, prev - 1));
    setProgressKey((k) => k + 1);
  }, []);

  useEffect(() => {
    if (isPaused || activities.length === 0) return;
    const duration = activities[currentIndex]
      ? getSlideDuration(activities[currentIndex])
      : DEFAULT_SLIDE_DURATION_MS;

    timerRef.current = setTimeout(goNext, duration);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [currentIndex, isPaused, activities, goNext, progressKey]);

  // ── Keyboard ────────────────────────────────────────────────────────────────
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'ArrowRight' || e.key === ' ') goNext();
      else if (e.key === 'ArrowLeft') goPrev();
      else if (e.key === 'Escape') handleClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [goNext, goPrev, handleClose]);

  // ── Touch / swipe ───────────────────────────────────────────────────────────
  function handleTouchStart(e: React.TouchEvent) {
    touchStartXRef.current = e.touches[0].clientX;
  }

  function handleTouchEnd(e: React.TouchEvent) {
    if (touchStartXRef.current === null) return;
    const dx = e.changedTouches[0].clientX - touchStartXRef.current;
    touchStartXRef.current = null;
    if (Math.abs(dx) < 40) return; // ignore micro-taps — handled by tap zones
    if (dx < -40) goNext();
    else if (dx > 40) goPrev();
  }

  // ── Pause on hold ───────────────────────────────────────────────────────────
  function handleLongPressStart() {
    setIsPaused(true);
    setShowPauseIcon(true);
  }

  function handleLongPressEnd() {
    setIsPaused(false);
    setProgressKey((k) => k + 1);
    if (pauseIconTimerRef.current) clearTimeout(pauseIconTimerRef.current);
    pauseIconTimerRef.current = setTimeout(() => setShowPauseIcon(false), 1000);
  }

  // ── Render ──────────────────────────────────────────────────────────────────
  return createPortal(
    <div
      className={styles.overlay}
      role='dialog'
      aria-modal='true'
    >
      <div
        className={styles.frame}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {/* Loading */}
        {loading && (
          <div className={styles.center}>
            <Spinner
              animation='border'
              variant='light'
              size='sm'
            />
          </div>
        )}

        {/* Error */}
        {!loading && error && (
          <div className={styles.center}>
            <span className={styles.emptyMessage}>{error}</span>
          </div>
        )}

        {/* Empty */}
        {!loading && !error && activities.length === 0 && (
          <div className={styles.center}>
            <span className={styles.emptyMessage}>
              No activities in this trip yet.
            </span>
          </div>
        )}

        {/* Slides */}
        {!loading && activities.length > 0 && (
          <>
            {/* Progress bar */}
            <div className={styles.progressBar}>
              {activities.map((_, idx) => (
                <div
                  key={idx}
                  className={styles.progressSegment}
                >
                  <div
                    key={`${idx}-${progressKey}`}
                    className={`${styles.progressFill}${
                      idx < currentIndex
                        ? ` ${styles.done}`
                        : idx === currentIndex && !isPaused
                          ? ` ${styles.active}`
                          : ''
                    }`}
                    style={
                      {
                        '--duration': `${
                          idx === currentIndex
                            ? getSlideDuration(activities[idx])
                            : DEFAULT_SLIDE_DURATION_MS
                        }ms`
                      } as React.CSSProperties
                    }
                  />
                </div>
              ))}
            </div>

            {/* Counter */}
            <div className={styles.counter}>
              {currentIndex + 1} / {activities.length}
            </div>

            {/* Slides */}
            <div className={styles.slidesContainer}>
              {activities.map((activity, idx) => (
                <ReelSlide
                  key={activity.id ?? idx}
                  activity={activity}
                  isVisible={idx === currentIndex}
                  cardSlideMap={cardSlideMap}
                />
              ))}
            </div>

            {/* Tap zones */}
            <div className={styles.tapZones}>
              <div
                className={styles.tapPrev}
                onClick={goPrev}
                onMouseDown={handleLongPressStart}
                onMouseUp={handleLongPressEnd}
                onTouchStart={handleLongPressStart}
                onTouchEnd={(e) => {
                  handleLongPressEnd();
                  handleTouchEnd(e);
                }}
              />
              <div
                className={styles.tapNext}
                onClick={goNext}
                onMouseDown={handleLongPressStart}
                onMouseUp={handleLongPressEnd}
                onTouchStart={handleLongPressStart}
                onTouchEnd={(e) => {
                  handleLongPressEnd();
                  handleTouchEnd(e);
                }}
              />
            </div>

            {/* Pause indicator */}
            {showPauseIcon && (
              <div className={styles.pauseIndicator}>
                {isPaused ? <Pause size={28} /> : <Play size={28} />}
              </div>
            )}
          </>
        )}

        {/* Close button — always visible */}
        <button
          type='button'
          className={styles.closeBtn}
          onClick={handleClose}
          aria-label='Close reel'
        >
          <X size={18} />
        </button>
      </div>
    </div>,
    document.body
  );
}

// ── ReelSlide sub-component ───────────────────────────────────────────────────

interface ReelSlideProps {
  activity: ITripActivity;
  isVisible: boolean;
  cardSlideMap: Map<string, string>;
}

function ReelSlide({ activity, isVisible, cardSlideMap }: ReelSlideProps) {
  const imageUrl = getSlideImageUrl(activity, cardSlideMap);
  const isVideo = activity.displayState?.displayMode === 'video_clip';
  const hasImage = !!imageUrl;

  return (
    <div className={`${styles.slide}${isVisible ? ` ${styles.visible}` : ''}`}>
      {/* Background */}
      {hasImage ? (
        <AuthenticatedImage
          src={imageUrl}
          alt={activity.title ?? ''}
          className={styles.slideBg}
        />
      ) : (
        <div className={styles.slideSkeleton} />
      )}

      {/* Video clip indicator */}
      {isVideo && (
        <div className={styles.videoBadge}>
          <div className={styles.playCircle}>
            <Play
              size={32}
              fill='white'
              color='white'
            />
          </div>
          <span className={styles.videoLabel}>Video Clip</span>
        </div>
      )}

      {/* Caption */}
      <div className={styles.slideCaption}>
        <p className={styles.slideTitle}>{activity.title}</p>
        {activity.type && <p className={styles.slideType}>{activity.type}</p>}
      </div>
    </div>
  );
}
